import { PropsWithChildren, useLayoutEffect } from 'react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import { Editor, posToDOMRect } from '@tiptap/core';
import { getMarkRange, getMarkType } from '@tiptap/react';

interface IProps {
  editor: Editor;
  open: boolean;
}

/**
 * This is custom implementation of Bubble Menu (https://tiptap.dev/docs/editor/api/extensions/bubble-menu) based
 * on the wonderful Floating UI library. The original Bubble Menu uses tippy.js which is less flexible in controlling
 * position and quite old (last update: 2022). It uses reactive flag (open) instead of callback which is more
 * React-friendly
 *
 * Extended from:
 * https://github.com/ueberdosis/tiptap/issues/2305#issuecomment-1020665146
 * https://github.com/ueberdosis/tiptap/issues/2305#issuecomment-1894184891
 *
 * @param editor - TipTap editor instance
 * @param open - if true - the menu is displayed
 * @param children - the menuc content
 */
export const FloatingMenu = ({
  editor,
  open,
  children,
}: PropsWithChildren<IProps>) => {
  const { view, state } = editor;
  const { floatingStyles, refs } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [
      offset({ mainAxis: 8 }),
      shift({
        padding: 8,
      }),
      flip({
        padding: 8,
        fallbackPlacements: ['bottom-start', 'bottom', 'top'],
      }),
    ],
  });

  useLayoutEffect(() => {
    refs.setReference({
      getBoundingClientRect() {
        const { ranges } = state.selection;
        // Svilen: My understanding is the ProseMirror supports multiple selections thus ranges is array
        // the lines below combines all selections as it was one continuous selection
        // (joining gaps if any)
        const from = Math.min(...ranges.map((range) => range.$from.pos));
        const to = Math.max(...ranges.map((range) => range.$to.pos));

        // Special case for links: position the menu anchored to the start of the link
        // Otherwise, it moves with the cursor and it is distracting.
        if (state.selection.empty) {
          const range = getMarkRange(
            view.state.doc.resolve(from),
            getMarkType('link', state.schema),
          );
          if (range) {
            return posToDOMRect(view, range.from, range.to);
          }
        }

        // Fallback: show the menu relative to the rectangle that from / to defines
        return posToDOMRect(view, from, to);
      },
    });
  }, [refs.reference, view, refs, state.selection, state.schema]);

  if (!open) {
    return null;
  }
  // additional z-index is required because TipTap area is position: relative
  const style = { ...floatingStyles, zIndex: 1 };
  return (
    <div ref={refs.setFloating} style={style}>
      {children}
    </div>
  );
};
